/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { animated, useSpring } from '@react-spring/web';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectHorses, selectPlayers, uiLogger, useTranslation } from '~/app';
import { cn } from '~/utils/styles';

import { ReactComponent as BonusCardsIcon } from './icons/bonus-cards.svg';
import { ReactComponent as HandicapFailureIcon } from './icons/handicap-failure.svg';
import { ReactComponent as HorseDroppedIcon } from './icons/horse-dropped.svg';
import { ReactComponent as NewRoundIcon } from './icons/new-round.svg';
import { ReactComponent as PlayerJoinedIcon } from './icons/player-joined.svg';
import {
  useBonusCardTrigger,
  useCardAboveHandiTrigger,
  useHorseDroppedTrigger,
  usePlayerJoinedTrigger,
  usePlayerLeftTrigger,
  usePlayerSkippedTrigger,
  useStartRoundTrigger,
} from './triggers';

// Action type enum for tracking which icon to show
type ActionType =
  | 'horseDropped'
  | 'newRound'
  | 'handicapFailure'
  | 'bonusCards'
  | 'playerJoined'
  | 'playerLeft'
  | 'playerSkipped';

interface ActionMessage {
  text: string;
  type: ActionType;
}

const ActionUI = () => {
  const { t } = useTranslation();
  const timeout = useRef<number | undefined>(undefined);
  const horses = useSelector(selectHorses);
  const players = useSelector(selectPlayers);

  const [actionMessage, setActionMessage] = useState<ActionMessage | undefined>();

  const animateText = useCallback(
    (value: string, type: ActionType) => {
      if (timeout.current !== undefined) {
        if (actionMessage?.text === value && actionMessage?.type === type) return;
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }
      setActionMessage({ text: value, type });
      timeout.current = window.setTimeout(() => {
        setActionMessage(undefined);
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }, 3000);
    },
    [actionMessage],
  );

  // Cleanup effect to ensure proper reset
  useEffect(() => {
    return () => {
      if (timeout.current !== undefined) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }
      setActionMessage(undefined);
    };
  }, []);

  // Listen for custom events from debug menu
  useEffect(() => {
    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { type, text } = customEvent.detail;
      uiLogger.debug({ type, text }, 'ActionUI received custom event');
      animateText(text, type as ActionType);
    };

    window.addEventListener('action-ui-trigger', handleCustomEvent);

    return () => {
      window.removeEventListener('action-ui-trigger', handleCustomEvent);
    };
  }, [animateText]);

  // React-spring animation for the text container
  const textSpring = useSpring({
    opacity: actionMessage ? 1 : 0,
    scale: actionMessage ? 1 : 0,
    config: {
      tension: 300,
      friction: 20,
      mass: 1,
    },
  });

  // React-spring animation for the background
  const backgroundSpring = useSpring({
    opacity: actionMessage ? 0.6 : 0,
    config: {
      tension: 400,
      friction: 30,
    },
  });

  /* dropped horses */
  useHorseDroppedTrigger(
    useCallback(
      (horseIndex) => {
        if (players && horses && horses[horseIndex] && players[horses[horseIndex].playerId]) {
          const playerName = players[horses[horseIndex].playerId].name;
          animateText(
            t('game_messageHorseDropped', { placeholders: { playerName } }),
            'horseDropped',
          );
        }
      },
      [animateText, horses, players, t],
    ),
  );

  /* new round started message */
  useStartRoundTrigger(
    useCallback(
      (round: number) => animateText(`Runde ${round + 1} gestartet`, 'newRound'),
      [animateText],
    ),
  );

  /* selected card too high for handicap */
  useCardAboveHandiTrigger(
    useCallback(
      (horseIndex) => {
        if (players && horses && horses[horseIndex] && players[horses[horseIndex].playerId]) {
          const playerName = players[horses[horseIndex].playerId].name;
          animateText(
            t('game_messageHandicap', { placeholders: { playerName } }),
            'handicapFailure',
          );
        }
      },
      [animateText, horses, players, t],
    ),
  );

  /* player uses bonuscards */
  useBonusCardTrigger(
    useCallback(
      (horseIndex: number) => {
        const horse = horses.find((h) => h.index === horseIndex);
        const playerName = horse ? players[horse.playerId].name : '';
        animateText(t('game_messageBonuscards', { placeholders: { playerName } }), 'bonusCards');
      },
      [animateText, horses, players, t],
    ),
  );

  /* player joined remote game */
  usePlayerJoinedTrigger(
    useCallback(
      (playerName: string) => {
        animateText(
          t('game_messagePlayerJoined', { placeholders: { playerName } }),
          'playerJoined',
        );
      },
      [animateText, t],
    ),
  );

  /* player left remote game */
  usePlayerLeftTrigger(
    useCallback(
      (playerName: string) => {
        animateText(t('game_messagePlayerLeft', { placeholders: { playerName } }), 'playerLeft');
      },
      [animateText, t],
    ),
  );

  /* player skipped due to no valid moves */
  usePlayerSkippedTrigger(
    useCallback(
      (horseIndex: number) => {
        if (players && horses && horses[horseIndex] && players[horses[horseIndex].playerId]) {
          const playerName = players[horses[horseIndex].playerId].name;
          animateText(
            t('game_messagePlayerSkipped', { placeholders: { playerName } }),
            'playerSkipped',
          );
        }
      },
      [animateText, horses, players, t],
    ),
  );

  // Function to get the appropriate icon based on action type
  const getActionIcon = (type: ActionType) => {
    switch (type) {
      case 'horseDropped':
        return <HorseDroppedIcon className="h-12 w-12 text-red-400" />;
      case 'newRound':
        return <NewRoundIcon className="h-12 w-12 text-blue-400" />;
      case 'handicapFailure':
        return <HandicapFailureIcon className="h-12 w-12 text-yellow-400" />;
      case 'bonusCards':
        return <BonusCardsIcon className="h-12 w-12 text-green-400" />;
      case 'playerJoined':
        return <PlayerJoinedIcon className="h-12 w-12 text-purple-400" />;
      case 'playerLeft':
        return <PlayerJoinedIcon className="h-12 w-12 text-red-400" />;
      case 'playerSkipped':
        return <HorseDroppedIcon className="h-12 w-12 text-orange-400" />;
      default:
        return null;
    }
  };

  return (
    <div className={cn('pointer-events-none absolute inset-0 backface-hidden')}>
      {/* Animated background */}
      <animated.div
        style={backgroundSpring}
        className="absolute inset-0 bg-black backdrop-blur-sm"
      />

      {/* Animated text container */}
      {actionMessage && (
        <animated.div
          style={textSpring}
          className="absolute top-1/2 left-1/2 z-0 mx-4 max-w-2xl -translate-x-1/2 -translate-y-1/2 transform rounded-xl border border-black/50 bg-[#00000055] px-8 py-6 shadow-2xl backdrop-blur-sm"
        >
          <div className="text-center">
            <div className="mb-3 flex items-center justify-center">
              {getActionIcon(actionMessage.type)}
            </div>
            <div className="text-lg leading-tight font-bold text-white drop-shadow-lg md:text-xl lg:text-2xl">
              {actionMessage.text}
            </div>
          </div>
        </animated.div>
      )}
    </div>
  );
};

export default memo(ActionUI);
